// src/pages/ScanQualityPage.jsx
// Routes: /quality-check (queue) and /quality-check/:recordId (one report) — health navigator / admin.
//
// A report that prints one result column per draw date is also held here, so a navigator confirms each date before it is split into one
// record per draw (a digital PDF has nothing to disagree about; a scan can have both).
// A scanned report is read twice, independently (a text reader and a second, AI reader looking at a de-identified copy of the results
// table). When the two disagree the report is held back: nothing from it is shown to the patient or the doctor until a navigator settles
// every difference here. Each decision is recorded, with both original readings, in an audit trail that cannot be edited.
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import EmptyState from '../components/EmptyState';
import IdentityMismatchModal from '../components/healthrecords/IdentityMismatchModal';
import { scanQualityAPI, healthRecordAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  KIND_LABEL, CHOICES, ACTION_LABEL, decisionError, openCount, saveState, uncheckedLabel, columnLabel, columnDatesState, latestColumnIndex, gridRowFor, cellView, withTypedDates,
  placedPagesOf, newRowError, EMPTY_DRAFT, approvedStatusLabel, CULTURE_RESULTS, isCultureName, cultureDraft, numericDraft, summaryRows,
} from '../utils/scanQuality';

const formatWhen = (iso) => (iso ? new Date(iso).toLocaleString() : '');

function Reading({ title, reading }) {
  return (
    <div className="card-row" style={{ flex: 1, minWidth: 200, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
      <div className="field-label">{title}</div>
      {reading ? (
        <>
          <div className="field-text" style={{ fontWeight: 600 }}>{reading.name}</div>
          <div className="page-title" style={{ fontSize: 22 }}>
            {reading.value}{reading.flag ? ` ${reading.flag}` : ''} <span className="field-text">{reading.unit || ''}</span>
          </div>
          {reading.lo != null && reading.hi != null && (
            <div className="field-text">Reference range {reading.lo} – {reading.hi}</div>
          )}
        </>
      ) : (
        <div className="field-text">Not found by this reader</div>
      )}
    </div>
  );
}

const CELL_STYLE = { padding: '4px 10px', textAlign: 'left', verticalAlign: 'top' };
const OPEN_STYLE = { background: 'var(--amber-bg)', fontWeight: 600 };

/** One value cell: the agreed value, both readings when they differ, or the navigator's decision once made. */
function GridValue({ cell, reader }) {
  const v = cellView(cell);
  if (v.kind === 'none') return <span className="field-text">—</span>;
  if (v.kind === 'agreed') return <span>{reader === 'ai' ? v.ai : v.text}</span>;
  if (v.kind === 'settled') return <span>{v.settled === 'ignored' ? 'ignored' : v.settled} <span className="field-text">(decided)</span></span>;
  return <span>{reader === 'ai' ? v.ai : v.text}</span>;
}

/**
 * One marker across every draw date, with what each reader read at each: the quickest way to see which date a difference is in and what
 * the other dates say. The cell being decided is highlighted.
 */
function DateGrid({ row, columns, focusId }) {
  return (
    <div className="table-wrap" style={{ marginTop: 6 }}>
      <table>
        <thead>
          <tr><th style={CELL_STYLE}>{row.name}{row.unit ? ` (${row.unit})` : ''}{row.lo != null && row.hi != null ? ` · ${row.lo} – ${row.hi}` : ''}</th>
            {columns.map((c) => <th key={c.index} style={CELL_STYLE}>{columnLabel(columns, c.index)}</th>)}</tr>
        </thead>
        <tbody>
          {[['text', 'Text reader (OCR)'], ['ai', 'Second reader (AI)']].map(([reader, label]) => (
            <tr key={reader}>
              <td style={CELL_STYLE} className="field-label">{label}</td>
              {columns.map((c) => {
                const cell = (row.cells || []).find((x) => x.col === c.index);
                const focus = cell && cell.differenceId === focusId;
                const open = cell && cellView(cell).kind === 'open';
                return (
                  <td key={c.index} style={{ ...CELL_STYLE, ...(open ? OPEN_STYLE : {}), ...(focus ? { outline: '2px solid var(--brand-accent)' } : {}) }}>
                    <GridValue cell={cell} reader={reader} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DifferenceCard({ diff, decision, onChange, columns, gridRow }) {
  const open = diff.status === 'OPEN';
  const choices = CHOICES[diff.kind] || [];
  const picked = decision?.action;
  const choice = choices.find((c) => c.action === picked);
  const err = decisionError(diff.kind, decision);
  return (
    <section className="card fade-in-up" aria-label={`Difference ${diff.seq + 1}`}>
      <div className="card-title">
        Page {diff.page}{columns.length > 1 ? ` · ${columnLabel(columns, diff.col)} column` : ''} · {KIND_LABEL[diff.kind]}
        {!open && <span className="badge badge-active">Decided</span>}
      </div>
      {columns.length > 1 && gridRow ? (
        <DateGrid row={gridRow} columns={columns} focusId={diff.id} />
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Reading title="Text reader (OCR)" reading={diff.ocr} />
          <Reading title="Second reader (AI, de-identified image)" reading={diff.claude} />
        </div>
      )}

      {open ? (
        <fieldset className="form-group" style={{ border: 0, padding: 0, marginTop: 12 }}>
          <legend className="form-label">What does the original report say?</legend>
          {choices.map((c) => (
            <label key={c.action} className="field-text" style={{ display: 'block', margin: '4px 0' }}>
              <input type="radio" name={`d-${diff.id}`} checked={picked === c.action}
                onChange={() => onChange({ ...decision, action: c.action })} /> {c.label}
            </label>
          ))}
          {choice?.needsValue && (
            <input className="form-input" inputMode="decimal" placeholder="Number as printed, e.g. 13.5" aria-label="Number from the report"
              value={decision?.value ?? ''} onChange={(e) => onChange({ ...decision, value: e.target.value })} style={{ marginTop: 6, maxWidth: 240 }} />
          )}
          {(choice?.needsNote || picked) && (
            <input className="form-input" placeholder={choice?.needsNote ? 'Why? (required)' : 'Note (optional)'} aria-label="Note"
              value={decision?.note ?? ''} onChange={(e) => onChange({ ...decision, note: e.target.value })} style={{ marginTop: 6 }} />
          )}
          {picked && err && <div className="alert alert-warning" role="alert" style={{ marginTop: 6 }}>{err}</div>}
        </fieldset>
      ) : (
        <div className="field-text" style={{ marginTop: 10 }}>
          {ACTION_LABEL[diff.action]}{diff.chosenValue ? `: ${diff.chosenValue}` : ''}{diff.note ? ` — “${diff.note}”` : ''}
        </div>
      )}
    </section>
  );
}

function Waiting() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    scanQualityAPI.queue()
      .then((r) => live && setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => live && setError('Could not load the quality-check queue.'));
    return () => { live = false; };
  }, []);

  if (error) return <div className="alert alert-error" role="alert">{error}</div>;
  if (!items) return <div className="skeleton" style={{ height: 120 }} />;
  if (items.length === 0) {
    return <EmptyState framed icon="check" title="Nothing waiting" description="No scanned report needs a quality check right now." />;
  }
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th>Patient</th><th>Report</th><th>Waiting since</th><th>Differences</th><th>Agreed rows</th><th>Not cross-checked</th><th /></tr></thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.recordId}>
              <td>{i.patientName}</td>
              <td>{i.fileName}{i.columnCount > 1 && <> <span className="badge badge-pending">{i.columnCount} draw dates</span></>}</td>
              <td>{formatWhen(i.waitingSince)}</td>
              <td><span className="badge badge-pending">{i.openCount}</span></td>
              <td>{i.agreedCount}{i.agreedConfirmed ? ' (confirmed)' : ''}</td>
              <td>{i.uncheckedCount > 0 ? `${i.uncheckedCount} page(s)` : '—'}</td>
              <td><Link className="btn btn-primary btn-sm" to={`/quality-check/${i.recordId}`}>Do QA</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Checks a navigator has finished: when each was approved and by whom, and whether the report has been produced since. */
const TRIAGE_RUNNING = ['EXTRACTING', 'EXTRACTED', 'READING_SCAN'];
const POLL_MS = 5000;
const POLL_LIMIT = 36;           // about three minutes: triage of a settled report normally finishes well inside that

function Approved() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [polls, setPolls] = useState(0);
  const { toast } = useToast();
  const [busyId, setBusyId] = useState(null);
  const [mismatch, setMismatch] = useState(null);       // { item, extractedText, data } while the identity dialog is open
  useEffect(() => {
    let live = true;
    scanQualityAPI.approved()
      .then((r) => live && setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => live && setError('Could not load the approved checks.'));
    return () => { live = false; };
  }, [polls]);
  // Triage starts by itself when a check is approved: keep the list fresh while any report is still being triaged, so it turns into
  // "Triaged" (or "Needs identity confirmation") without a manual refresh.
  const running = Array.isArray(items) && items.some((i) => TRIAGE_RUNNING.includes(i.recordStatus));
  useEffect(() => {
    if (!running || polls >= POLL_LIMIT) return undefined;
    const t = setTimeout(() => setPolls((n) => n + 1), POLL_MS);
    return () => clearTimeout(t);
  }, [running, polls]);

  // Same check the upload flow runs: submitting the record's own text again returns the name/age the report prints against the profile.
  const openConfirmIdentity = async (item) => {
    if (busyId) return;
    setBusyId(item.recordId);
    try {
      const full = await healthRecordAPI.get(item.patientId, item.recordId);
      const extractedText = full.data.extractedText;
      if (!extractedText) { toast.error('This report has no text to check. Re-upload it from the patient\'s Health records.'); return; }
      try {
        await healthRecordAPI.saveExtractedText(item.patientId, item.recordId, extractedText);
        setPolls((n) => n + 1);                          // nothing to confirm any more (the profile was corrected): just refresh
      } catch (err) {
        if (err.response?.status === 409 && err.response.data?.error === 'IDENTITY_MISMATCH') setMismatch({ item, extractedText, data: err.response.data });
        else throw err;
      }
    } catch {
      toast.error('Could not check this report. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const confirmIdentity = async (comment) => {
    const { item, extractedText } = mismatch;
    // The server starts triage itself once identity is confirmed (startTriage), so nothing is left waiting for "Run triage" and closing this
    // page does not lose it. The list below refreshes while it runs.
    await healthRecordAPI.saveExtractedText(item.patientId, item.recordId, extractedText, true, comment, true);
    setMismatch(null);
    toast.success('Identity confirmed. Triage has started.');
    setPolls(0);
    setItems((prev) => (prev || []).map((i) => (i.recordId === item.recordId ? { ...i, recordStatus: 'EXTRACTING' } : i)));
  };

  if (error) return <div className="alert alert-error" role="alert">{error}</div>;
  if (!items) return <div className="skeleton" style={{ height: 120 }} />;
  if (items.length === 0) {
    return <EmptyState framed icon="check" title="Nothing approved yet" description="Checks you finish will be listed here with the date they were approved." />;
  }
  return (
    <>
    {mismatch && (
      <IdentityMismatchModal
        mismatch={{
          extractedName: mismatch.data.extractedName, extractedAge: mismatch.data.extractedAge,
          profileName: mismatch.data.profileName, profileAge: mismatch.data.profileAge,
        }}
        title="Confirm this report's identity"
        description="This report's own details don't match the patient's profile. Confirm to proceed with triage anyway, or close this and re-upload or delete the document instead."
        cancelLabel="Not now"
        onConfirm={confirmIdentity}
        onCancel={() => setMismatch(null)}
      />
    )}
    <div className="table-wrap">
      <table>
        <thead><tr><th>Patient</th><th>Report</th><th>Approved on</th><th>Approved by</th><th>Decisions</th><th>Report</th><th /></tr></thead>
        <tbody>
          {items.map((i) => {
            const st = approvedStatusLabel(i.recordStatus);
            return (
              <tr key={i.recordId}>
                <td>{i.patientName}</td>
                <td>
                  {i.fileName}
                  {i.columnCount > 1 && <> <span className="badge badge-pending">{i.columnCount} draw dates</span></>}
                  {i.recordsCreated > 0 && <div className="field-text">split into {i.recordsCreated + 1} records</div>}
                </td>
                <td>{formatWhen(i.approvedAt)}</td>
                <td>{i.approvedBy || '—'}</td>
                <td>{i.decisions}{i.addedResults > 0 ? ` + ${i.addedResults} added` : ''}</td>
                <td>
                  <span className={`badge ${st.tone === 'ok' ? 'badge-active' : 'badge-pending'}`}>{st.text}</span>
                  {i.recordStatus === 'AWAITING_IDENTITY_CONFIRMATION' && <div className="field-text">The name or age on the report differs from the profile. Use Confirm identity if it is the patient's report.</div>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {i.recordStatus === 'AWAITING_IDENTITY_CONFIRMATION' && (
                    <><button type="button" className="btn btn-primary btn-sm" disabled={busyId === i.recordId} onClick={() => openConfirmIdentity(i)}>
                      {busyId === i.recordId ? 'Checking…' : 'Confirm identity'}
                    </button>{' '}</>
                  )}
                  <Link className="btn btn-ghost btn-sm" to={`/quality-check/${i.recordId}`}>View resolution</Link>{' '}
                  <Link className="btn btn-ghost btn-sm" to={`/patients/${i.patientId}?tab=${encodeURIComponent('Health Records')}`}>Health records</Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function Queue() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'approved' ? 'approved' : 'waiting';
  const setTab = (key) => setParams(key === 'approved' ? { tab: 'approved' } : {}, { replace: true });
  return (
    <>
      <div className="tabs-bar" role="tablist">
        {[['waiting', 'Waiting for QA'], ['approved', 'Approved']].map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={`tab-btn ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>
      {tab === 'waiting' ? <Waiting /> : <Approved />}
    </>
  );
}

const pagesText = (pages) => (pages.length === 1 ? `Page ${pages[0]}` : `Pages ${pages.join(', ')}`);

/** The result rows a navigator is adding or has added: nothing here is read by a reader, so each carries who/why in the audit trail. */
/** The few lines of a suggested page, laid out: section, test name, labelled pairs, a status and the indented result. */
function FindingLines({ lines }) {
  const rows = summaryRows(lines);
  return (
    <div className="find-lines">
      {rows.map((r, i) => {
        if (r.kind === 'section') return <div key={i} className="find-sec">{r.text}</div>;
        if (r.kind === 'name') return <div key={i} className="find-name">{r.text}</div>;
        if (r.kind === 'status') return <div key={i}><span className="badge badge-pending">{r.text}</span></div>;
        if (r.kind === 'pair') return <div key={i} className="find-row"><span className="find-label">{r.label}</span><span className="find-value">{r.value}</span></div>;
        if (r.kind === 'result') return <div key={i} className="find-row find-result"><span className="find-label">{r.label}</span><span className="find-value">{r.value}</span></div>;
        return <div key={i} className="find-value">{r.text}</div>;
      })}
    </div>
  );
}

/**
 * One-click confirmation of a page the readers could not turn into a row: what it looks like (a blood or urine culture, or
 * procalcitonin) with the result to confirm or change. A culture's result is picked from a fixed list; procalcitonin is a number.
 */
function SuggestionConfirm({ page, name, suggested, unit, already, onConfirm }) {
  const [value, setValue] = useState(suggested || '');
  if (already) return <div className="field-text" style={{ marginTop: 6 }}>{name} added from this page.</div>;
  const numeric = Boolean(unit);
  return (
    <div className="form-row" style={{ '--cols': '1fr auto', alignItems: 'end', marginTop: 8 }}>
      <label className="form-group">
        <span className="form-label">
          This looks like {numeric ? name : `a ${name}`}. {numeric ? `Value on page ${page} (${unit})` : `Result on page ${page}`}
          {suggested ? '' : ' (not clear from the text, please choose)'}
        </span>
        {numeric ? (
          <input className="form-input" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
        ) : (
          <select className="form-input" value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Choose…</option>
            {CULTURE_RESULTS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </label>
      <button type="button" className="btn btn-ghost" disabled={!String(value).trim() || (numeric && !/^\d{1,9}(\.\d{1,6})?$/.test(String(value).trim()))}
        onClick={() => onConfirm(String(value).trim())}>Confirm result</button>
    </div>
  );
}

function AddedResults({ saved, pending, removedIds, columns, onRemoveSaved, onRemovePending }) {
  const shown = [...saved.filter((a) => !removedIds.includes(a.id)).map((a) => ({ ...a, key: a.id, saved: true })),
    ...pending.map((a, i) => ({ ...a, key: `p${i}`, idx: i, saved: false }))];
  if (shown.length === 0) return null;
  return (
    <div className="table-wrap" style={{ marginTop: 10 }}>
      <table>
        <thead><tr><th>Page</th>{columns.length > 1 && <th>Draw</th>}<th>Marker</th><th>Value</th><th>Unit</th><th>Range</th><th>Note</th><th /></tr></thead>
        <tbody>
          {shown.map((a) => (
            <tr key={a.key}>
              <td>{a.page}</td>{columns.length > 1 && <td>{columnLabel(columns, Number(a.col))}</td>}
              <td>{a.name}</td><td>{a.value}</td><td>{a.unit || ''}</td>
              <td>{a.lo !== '' && a.lo != null && a.hi !== '' && a.hi != null ? `${a.lo} – ${a.hi}` : ''}</td>
              <td>{a.note}{!a.saved && <span className="badge badge-pending" style={{ marginLeft: 6 }}>not saved yet</span>}</td>
              <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => (a.saved ? onRemoveSaved(a.id) : onRemovePending(a.idx))}>Remove</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Report({ recordId }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [decisions, setDecisions] = useState({});
  const [saving, setSaving] = useState(false);
  const [attest, setAttest] = useState(false);
  const [dates, setDates] = useState({});
  const [attestDates, setAttestDates] = useState(false);
  const [undatedPick, setUndatedPick] = useState(null);
  const [pendingAdds, setPendingAdds] = useState([]);        // results being added, not saved yet
  const [removedAddedIds, setRemovedAddedIds] = useState([]);   // saved added results marked for removal
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState('');

  const columns = withTypedDates(detail?.columns || [], dates, detail?.columnsConfirmed);
  const undatedPages = detail?.undatedPages || [];
  const textPages = detail?.textPages || [];
  // a page we can suggest a result for is shown (briefly) with its confirm control; only the rest are "pages with no results"
  const foundPages = textPages.filter((t) => t.suggestedName);
  const otherTextPages = textPages.filter((t) => !t.suggestedName);
  // has a result already been added (saved, or waiting to be saved) from this page?
  const addedFrom = (page) => [...(detail?.addedRows || []).filter((r) => !removedAddedIds.includes(r.id)), ...pendingAdds].some((r) => Number(r.page) === page);                    // pages with text but no results (a culture report)
  const placedPages = placedPagesOf(undatedPages, textPages.map((t) => t.page));
  // the navigator's pick, else the most recent draw as the suggested home of pages that show no dates
  const undatedColumn = Number.isInteger(undatedPick) ? undatedPick : latestColumnIndex(columns, dates);
  const saveOpts = {
    agreedConfirmed: detail?.agreedConfirmed, attest, columns, columnsConfirmed: detail?.columnsConfirmed ?? true,
    columnDates: dates, attestDates, undatedPages, undatedColumn,
    textPages: textPages.map((t) => t.page), pendingAdds, removedAddedIds,
  };

  const load = useCallback(async () => {
    try {
      const r = await scanQualityAPI.detail(recordId);
      setDetail(r.data);
      // pre-fill each column's date from what the readers found; the navigator confirms or corrects every one
      setDates((prev) => (Object.keys(prev).length ? prev
        : Object.fromEntries((r.data.columns || []).map((c) => [c.index, c.date || '']))));
      setError('');
    } catch (e) {
      setError(e?.response?.status === 403
        ? 'This report belongs to a patient who is not assigned to you, so only their navigator or an admin can review it.'
        : 'Could not load this report.');
    }
  }, [recordId]);
  useEffect(() => { load(); }, [load]);

  const openOriginal = async () => {
    try {
      const r = await healthRecordAPI.getDownloadUrl(detail.patientId, recordId);
      if (!window.open(r.data.downloadUrl, '_blank', 'noopener')) toast.error('Pop-up blocked — allow pop-ups and try again.');
    } catch {
      toast.error('Could not open the original report.');
    }
  };

  const save = async () => {
    const { resolutions, confirmNow, columnDates, undatedColumn: placedColumn, addRows, removeAddedRows, canSave } = saveState(detail.differences, decisions, saveOpts);
    if (!canSave) return;
    setSaving(true);
    try {
      const r = await scanQualityAPI.resolve(recordId, resolutions, confirmNow, columnDates, placedColumn, addRows, removeAddedRows);
      setDecisions({});
      setPendingAdds([]);
      setRemovedAddedIds([]);
      if (r.data.released) {
        toast.success(r.data.recordsCreated > 0
          ? `Checked. The report was split into ${r.data.recordsCreated + 1} records, one per draw date, and released. Triage has started.`
          : 'Checked. The report has been released and triage has started.');
        navigate('/quality-check?tab=approved');
        return;
      }
      toast.success(r.data.stillOpen > 0 ? `Saved. ${r.data.stillOpen} difference(s) still open.` : 'Saved.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save these decisions.');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="alert alert-error" role="alert">{error}</div>;
  if (!detail) return <div className="skeleton" style={{ height: 160 }} />;
  const remaining = openCount(detail.differences);
  const { resolutions, confirmNow, confirmDatesNow, invalid, canSave } = saveState(detail.differences, decisions, saveOpts);
  const ready = resolutions.length;
  const trend = columns.length > 1;
  // With several draws one marker is one table row holding a value per draw, while
  // agreedRows has one entry per value (per draw); counting those showed a 38-marker
  // report as 79. The count and the table both use the grid rows.
  const agreedGridRows = trend && detail.grid?.length > 0
    ? detail.grid.filter((r) => r.cells.some((c) => c.state === 'AGREED'))
    : null;
  const awaitingRelease = remaining > 0 || !detail.agreedConfirmed || (trend && !detail.columnsConfirmed);
  const datesCheck = columnDatesState(columns, dates);

  return (
    <>
      <Link className="back-link" to="/quality-check">← Back to the queue</Link>
      {detail.status === 'RESOLVED' && (
        <div className="alert alert-info" role="status">
          <strong>Approved{detail.approvedAt ? ` on ${formatWhen(detail.approvedAt)}` : ''}{detail.approvedBy ? ` by ${detail.approvedBy}` : ''}.</strong>
          <div>This is the record of the check. The report was released and nothing here can be changed.</div>
        </div>
      )}
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">{detail.patientName}</div>
          <div className="page-subtitle">
            {detail.fileName} · {detail.agreedCount} row(s) read the same by both readers · {remaining} difference(s) to settle.
            The patient and doctor see nothing from this report until you have settled every difference and confirmed the rows below{trend ? ' and the date of each column' : ''}.
          </div>
        </div>
        <button type="button" className="btn btn-ghost" onClick={openOriginal}>View original report</button>
      </div>

      {detail.uncheckedPages?.length > 0 && (() => {
        const textOnly = detail.uncheckedPages.filter((u) => textPages.some((t) => t.page === u.page));
        const other = detail.uncheckedPages.filter((u) => !textPages.some((t) => t.page === u.page));
        return (
          <div className="alert alert-warning" role="alert">
            <strong>{uncheckedLabel(detail.uncheckedPages)} could not be cross-checked automatically.</strong>
            {textOnly.length > 0 && (
              <div>
                {textOnly.length === 1 ? 'It holds' : 'They hold'} text, not numeric results, so there was nothing for either reader to compare.
                Check the original in case {textOnly.length === 1 ? 'it has' : 'they have'} a result the readers missed.
              </div>
            )}
            {other.length > 0 && (
              <div>
                The automatic second reading could not be used for {pagesText(other.map((u) => u.page)).toLowerCase()} (for example a QR code,
                signatures or show-through from the back of the sheet). Open the original report and check {other.length === 1 ? 'it' : 'them'} yourself before you confirm.
              </div>
            )}
            {other.some((u) => u.reason) && (
              <ul style={{ margin: '6px 0 0 18px' }}>
                {other.filter((u) => u.reason).map((u) => <li key={u.page}>Page {u.page}: {u.reason}</li>)}
              </ul>
            )}
            {other.some((u) => /could not be read/i.test(u.reason || '')) && (
              <div>
                <strong>This usually means a blurry or low-resolution photo.</strong> Ask the lab or the patient for the original PDF: it reads
                far more accurately than a photo, and most of the rows below would then match on their own.
              </div>
            )}
          </div>
        );
      })()}

      {foundPages.length > 0 && (
        <section className="card fade-in-up" aria-label="Results found on text pages">
          <div className="card-title">Results found on pages with no table ({foundPages.length})</div>
          <div className="field-text">
            These pages are not laid out as a results table, but each one reads like a result. Check the lines below against the
            original, confirm or change the value, and it is added as a marker.
          </div>
          {foundPages.map((t) => (
            <div key={t.page} className="find-card">
              <div className="find-page">Page {t.page}{addedFrom(t.page) ? ` · ${t.suggestedName} added` : ''}</div>
              <FindingLines lines={t.summary} />
              {detail.status !== 'RESOLVED' && (
                <SuggestionConfirm page={t.page} name={t.suggestedName} suggested={t.suggestedResult} unit={t.suggestedUnit}
                  already={addedFrom(t.page)}
                  onConfirm={(value) => setPendingAdds((prev) => [...prev, t.suggestedUnit
                    ? numericDraft(t.page, t.suggestedName, value, t.suggestedUnit)
                    : cultureDraft(t.page, t.suggestedName, value)])} />
              )}
            </div>
          ))}
        </section>
      )}

      {otherTextPages.length > 0 && (
        <section className="card fade-in-up" aria-label="Pages with no results">
          <div className="card-title">Pages with no results ({otherTextPages.length})</div>
          <div className="field-text">
            These pages hold text only and nothing could be suggested from them. If one has a result that should be
            tracked, add it below. Anything you do not turn into a marker stays with the report as <strong>Lab observations</strong>.
          </div>
          {otherTextPages.map((t) => (
            <details key={t.page} style={{ marginTop: 8 }} open>
              <summary>Page {t.page}</summary>
              <pre className="field-text" style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono)', margin: '6px 0 0' }}>{t.text}</pre>
            </details>
          ))}
        </section>
      )}

      {detail.status !== 'RESOLVED' && <section className="card fade-in-up" aria-label="Add a result">
        <div className="card-title">Add a result the readers missed</div>
        <details>
          <summary>A result neither reader found (for example a value printed beside a range such as “&lt; 35”)</summary>
          <div className="form-row" style={{ '--cols': trend ? '90px 190px 1fr' : '90px 1fr' }}>
            <label className="form-group">
              <span className="form-label">Page</span>
              <input className="form-input" inputMode="numeric" value={draft.page} onChange={(e) => setDraft({ ...draft, page: e.target.value })} placeholder={`1–${detail.pageCount}`} />
            </label>
            {trend && (
              <label className="form-group">
                <span className="form-label">Draw</span>
                <select className="form-input" value={draft.col} onChange={(e) => setDraft({ ...draft, col: e.target.value })}>
                  {columns.map((c) => <option key={c.index} value={String(c.index)}>{columnLabel(columns, c.index)}</option>)}
                </select>
              </label>
            )}
            <label className="form-group">
              <span className="form-label">Marker</span>
              <input className="form-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. SGOT (AST), Blood Culture, Urine Culture" />
            </label>
          </div>
          {isCultureName(draft.name) ? (
            <div className="form-row" style={{ '--cols': '1fr' }}>
              <label className="form-group">
                <span className="form-label">Culture result</span>
                <select className="form-input" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value, unit: '', lo: '', hi: '' })}>
                  <option value="">Choose…</option>
                  {CULTURE_RESULTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
          ) : (
          <div className="form-row" style={{ '--cols': 'repeat(4, 1fr)' }}>
            <label className="form-group">
              <span className="form-label">Value</span>
              <input className="form-input" inputMode="decimal" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} placeholder="38" />
            </label>
            <label className="form-group">
              <span className="form-label">Unit</span>
              <input className="form-input" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} placeholder="U/L" />
            </label>
            <label className="form-group">
              <span className="form-label">Range low</span>
              <input className="form-input" inputMode="decimal" value={draft.lo} onChange={(e) => setDraft({ ...draft, lo: e.target.value })} />
            </label>
            <label className="form-group">
              <span className="form-label">Range high</span>
              <input className="form-input" inputMode="decimal" value={draft.hi} onChange={(e) => setDraft({ ...draft, hi: e.target.value })} placeholder="35" />
            </label>
          </div>
          )}
          <div className="form-row" style={{ '--cols': '1fr' }}>
            <label className="form-group">
              <span className="form-label">Where you read it (required)</span>
              <input className="form-input" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="e.g. page 5 of the original, printed range “&lt; 35”" />
            </label>
          </div>
          {draftError && <div className="alert alert-warning" role="alert" style={{ marginTop: 6 }}>{draftError}</div>}
          <button type="button" className="btn btn-ghost" style={{ marginTop: 14 }} onClick={() => {
            const err = newRowError(draft, detail.pageCount, columns);
            setDraftError(err || '');
            if (err) return;
            setPendingAdds((prev) => [...prev, draft]);
            setDraft({ ...EMPTY_DRAFT, page: draft.page, col: draft.col });
          }}>Add this result</button>
        </details>
        <AddedResults saved={detail.addedRows || []} pending={pendingAdds} removedIds={removedAddedIds} columns={columns}
          onRemoveSaved={(id) => setRemovedAddedIds((prev) => [...prev, id])}
          onRemovePending={(i) => setPendingAdds((prev) => prev.filter((_, j) => j !== i))} />
      </section>}

      {trend && (
        <section className="card fade-in-up" aria-label="Draw dates">
          <div className="card-title">
            This report shows {columns.length} draws side by side
            {detail.columnsConfirmed && <span className="badge badge-active">Dates confirmed</span>}
          </div>
          <div className="field-text">
            Each column is a separate blood draw. When you release the report it is split into one record per date, and each record is
            triaged and shown on the patient&apos;s timeline at its own date. The most recent date stays on the upload.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
            {columns.map((c) => (
              <label key={c.index} className="form-group" style={{ minWidth: 180 }}>
                <span className="form-label">Column {c.index + 1}{c.label ? ` · printed “${c.label}”` : ''}</span>
                <input className="form-input" type="date" value={detail.columnsConfirmed ? (c.date || '') : (dates[c.index] ?? '')}
                  disabled={detail.columnsConfirmed} max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDates((prev) => ({ ...prev, [c.index]: e.target.value }))} />
              </label>
            ))}
          </div>
          {detail.narrowPages?.length > 0 && (
            <div className="alert alert-warning" role="alert" style={{ marginTop: 10 }}>
              {detail.narrowPages.map((n) => `Page ${n.page} shows ${n.columns} of the ${columns.length} dates`).join('; ')}. Its columns are matched to the
              dates left to right: check that against the original.
            </div>
          )}
          {placedPages.length > 0 && (
            <label className="form-group" style={{ display: 'block', marginTop: 10 }}>
              <span className="form-label">
                {undatedPages.length > 0 && `${pagesText(undatedPages)} ${undatedPages.length === 1 ? 'shows' : 'show'} one value per marker with no date. `}
                {textPages.length > 0 && `${pagesText(textPages.map((t) => t.page))} ${textPages.length === 1 ? 'holds' : 'hold'} text only, no results. `}
                Which draw {placedPages.length === 1 ? 'does it' : 'do they'} belong to?
              </span>
              {detail.columnsConfirmed ? (
                <div className="field-text">{columnLabel(columns, detail.undatedColumn)}</div>
              ) : (
                <select className="form-input" value={undatedColumn ?? ''} onChange={(e) => setUndatedPick(Number(e.target.value))} style={{ maxWidth: 280 }}>
                  <option value="" disabled>Choose a draw</option>
                  {columns.map((c) => (
                    <option key={c.index} value={c.index}>{dates[c.index] ? columnLabel([{ ...c, date: dates[c.index] }], c.index) : `Column ${c.index + 1}`}</option>
                  ))}
                </select>
              )}
            </label>
          )}
          {!detail.columnsConfirmed && (
            <>
              {!datesCheck.valid && <div className="alert alert-warning" role="alert" style={{ marginTop: 6 }}>{datesCheck.error}</div>}
              <label className="field-text" style={{ display: 'block', marginTop: 10 }}>
                <input type="checkbox" checked={attestDates} disabled={!datesCheck.valid || (placedPages.length > 0 && !Number.isInteger(undatedColumn))} onChange={(e) => setAttestDates(e.target.checked)} />{' '}
                I have checked each date against the original report
              </label>
            </>
          )}
        </section>
      )}

      {detail.differences.map((d) => (
        <DifferenceCard key={d.id} diff={d} decision={decisions[d.id]} columns={columns} gridRow={gridRowFor(detail.grid, d.id)}
          onChange={(x) => setDecisions((prev) => ({ ...prev, [d.id]: x }))} />
      ))}

      <section className="card fade-in-up" aria-label="Rows both readers agree on">
        <div className="card-title">
          Rows both readers agree on ({agreedGridRows ? agreedGridRows.length : detail.agreedRows.length})
          {detail.agreedConfirmed && <span className="badge badge-active">Confirmed</span>}
        </div>
        {detail.agreedRows.length === 0 ? (
          <div className="field-text">No rows were read the same by both readers.</div>
        ) : trend && detail.grid?.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Page</th><th>Marker</th><th>Unit</th><th>Reference range</th>
                  {columns.map((c) => <th key={c.index}>{columnLabel(columns, c.index)}</th>)}</tr>
              </thead>
              <tbody>
                {agreedGridRows.map((r, i) => (
                  <tr key={`${r.page}-${r.name}-${i}`}>
                    <td>{r.page}</td><td>{r.name}</td><td>{r.unit || ''}</td>
                    <td>{r.lo != null && r.hi != null ? `${r.lo} – ${r.hi}` : ''}</td>
                    {columns.map((c) => {
                      const cell = r.cells.find((x) => x.col === c.index);
                      const v = cellView(cell);
                      return (
                        <td key={c.index} style={v.kind === 'open' || v.kind === 'settled' ? OPEN_STYLE : undefined}>
                          {v.kind === 'agreed' ? v.text
                            : v.kind === 'settled' ? `${v.settled} (decided)`
                            : v.kind === 'open' ? <span title="The readers differ: decide it above">to settle</span> : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Page</th><th>Marker</th><th>Value</th><th>Unit</th><th>Reference range</th></tr></thead>
              <tbody>
                {detail.agreedRows.map((a, i) => (
                  <tr key={`${a.page}-${a.col}-${a.name}-${i}`}>
                    <td>{a.page}</td><td>{a.name}</td><td>{a.value}{a.flag ? ` ${a.flag}` : ''}</td><td>{a.unit || ''}</td>
                    <td>{a.lo != null && a.hi != null ? `${a.lo} – ${a.hi}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!detail.agreedConfirmed && (
          <label className="field-text" style={{ display: 'block', marginTop: 10 }}>
            <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />{' '}
            I have checked these values against the original report and they are correct
            {detail.uncheckedPages?.length > 0 ? `, and I have reviewed ${uncheckedLabel(detail.uncheckedPages)} in the original.` : '.'}
          </label>
        )}
      </section>

      {awaitingRelease && (
        <div className="toolbar">
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !canSave}>
            {saving ? 'Saving…' : (confirmNow || confirmDatesNow) && ready === 0 ? 'Confirm and release'
              : (confirmNow || confirmDatesNow) ? `Save ${ready} decision(s) and confirm` : `Save ${ready} decision(s)`}
          </button>
          {invalid && <span className="field-text">Finish the highlighted items first.</span>}
        </div>
      )}

      <section className="card fade-in-up" aria-label="Audit trail">
        <div className="card-title">Audit trail</div>
        {detail.audit.length === 0 ? (
          <div className="field-text">No decisions recorded yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>When</th><th>Marker</th><th>Text reader</th><th>Second reader</th><th>Decision</th><th>Chosen</th><th>Resolved by</th><th>Note</th></tr></thead>
              <tbody>
                {detail.audit.map((a, i) => (
                  <tr key={`${a.at}-${i}`}>
                    <td>{formatWhen(a.at)}</td><td>{a.marker}</td><td>{a.ocrValue || '—'}</td><td>{a.claudeValue || '—'}</td>
                    <td>{ACTION_LABEL[a.action]}</td><td>{a.chosenValue || '—'}</td><td>{a.resolvedBy || '—'}</td><td>{a.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default function ScanQualityPage() {
  const { recordId } = useParams();
  useEffect(() => {
    document.title = 'Quality check — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);
  return (
    <AppShell breadcrumbs={recordId
      ? [{ label: 'Quality check', to: '/quality-check' }, { label: 'Report' }]
      : [{ label: 'Quality check' }]}>
      {!recordId && (
        <div className="page-header fade-in-up">
          <div>
            <div className="page-title">Reports AI could not read with full confidence</div>
            <div className="page-subtitle">
              Also includes reports that show results from several dates. Patients and doctors see nothing from a report until you settle it.
            </div>
          </div>
        </div>
      )}
      {recordId ? <Report recordId={recordId} /> : <Queue />}
    </AppShell>
  );
}
